import * as React from 'react';

import { enumUnit, WeightDisplayUnit } from '@rematter/frontend-lib-api-client';
import { useLoadLineItemsManager } from '@rematter/modules/trade/hooks/useLoadLineItemsManager';
import { useSalesOrderLineItemsData } from '@rematter/modules/trade/hooks/useSalesOrderLineItemsData';
import { useTranslations } from '@rematter/modules/trade/i18n/useTranslations';
import { cn, Widget } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';
import { v4 as uuidv4 } from 'uuid';

import { $materialSortingRules } from '../../../_state/loadStaged.state';
import { useInventoryTags } from '../../../hooks/useInventoryTags';
import { useWeightUnit } from '../../../hooks/useWeightUnit';
import { useLoadStagedAutoReset } from '../hooks/useLoadStagedAutoReset';
import { useLoadStagedData } from '../hooks/useLoadStagedData';
import { usePricingUnitSync } from '../hooks/useLoadStagedSync';

import { LineItemPhotoModal } from './LineItemPhotoModal';
import { LoadStagedFooter } from './LoadStagedFooter';
import { MaterialsWidget } from './MaterialsWidget';
import { RequestedMaterialsList } from './RequestedMaterialsList';
import { ScaleAndCameraWidget } from './ScaleAndCameraWidget';
import { StagedItemsList } from './StagedItemsList';

import {
  $inventoryItemsById,
  $loadForm,
} from '~/modules/trade/_state/loadForm.state';
import { enumLoadWeightType, LoadFormState } from '~/modules/trade/types/load';
import { createDefaultWeights } from '~/modules/trade/utils/weight';
import {
  $defaultUnitSystemUnit,
  $salesDefaultWeightDisplayUnit,
} from '~/shared/state/settings.state';

type LoadStagedProps = {
  weightDisplayUnitPreference?: WeightDisplayUnit;
  disabled?: boolean;
  onSaveUpdates: (
    tab?: 'load-info' | 'materials',
    data?: LoadFormState,
    onlyDatesAndEntities?: boolean,
  ) => void;
  onDiscard: (onlyDiscardDatesAndEntities?: boolean) => void;
  isSaving?: boolean;
};

const loadMaterialsFormSelector = (state: LoadFormState) => ({
  salesOrderNumber: state.salesOrderNumber,
});

export const LoadStaged = ({
  weightDisplayUnitPreference,
  disabled,
  onSaveUpdates,
  onDiscard,
  isSaving,
}: LoadStagedProps) => {
  const t = useTranslations();
  const { stagedLineItems, requestedLineItems, stagedPhase } =
    useLoadStagedData();
  const { inventoryTagOptionsByItemId } = useInventoryTags();
  const { salesOrderLineItemsData, getSeparatedLineItems } =
    useSalesOrderLineItemsData();

  const salesOrderLineItemsList = React.useMemo(
    () => getSeparatedLineItems(requestedLineItems).salesOrderLineItems,
    [requestedLineItems, getSeparatedLineItems],
  );

  const salesOrderItemIds = React.useMemo(
    () => new Set(salesOrderLineItemsData.map((item) => item.item.id)),
    [salesOrderLineItemsData],
  );

  const selectedFormAtom = React.useMemo(
    () => selectAtom($loadForm, loadMaterialsFormSelector, isEqual),
    [],
  );
  const loadForm = useAtomValue(selectedFormAtom);

  const highlightedMaterialIds = React.useMemo(
    () => salesOrderLineItemsData.map((lineItem) => lineItem.item.id),
    [salesOrderLineItemsData],
  );

  const highlightedSectionTitle = React.useMemo(() => {
    if (!loadForm.salesOrderNumber) return undefined;
    return `${t.loadStaged.salesOrderMaterials} • #${loadForm.salesOrderNumber}`;
  }, [loadForm.salesOrderNumber, t.loadStaged.salesOrderMaterials]);

  // Compute requestedItemIds - items that have a requested weight
  const requestedItemIds = React.useMemo(
    () =>
      new Set(
        requestedLineItems
          .filter(
            (item) =>
              item.itemId && item.weights?.[enumLoadWeightType.Net]?.value,
          )
          .map((item) => item.itemId as string),
      ),
    [requestedLineItems],
  );

  // Sync sorting rules to atom for child components
  const setMaterialSortingRules = useSetAtom($materialSortingRules);

  React.useEffect(() => {
    setMaterialSortingRules({ salesOrderItemIds, requestedItemIds });
  }, [salesOrderItemIds, requestedItemIds, setMaterialSortingRules]);

  const { addItem, updateItem, deleteItem } = useLoadLineItemsManager({
    salesOrderLineItems: salesOrderLineItemsList,
    skipDuplicateValidation: true,
  });

  const inventoryItemsById = useAtomValue($inventoryItemsById);
  const facilityDefaultUnit = useAtomValue($defaultUnitSystemUnit);
  const defaultWeightDisplayUnit = useAtomValue($salesDefaultWeightDisplayUnit);
  const { getLineItemWeightUnit } = useWeightUnit({
    weightDisplayUnitPreference,
    defaultWeightDisplayUnit,
    facilityDefaultUnit,
  });

  const [newlyStagedItemId, setNewlyStagedItemId] = React.useState<
    string | null
  >(null);

  useLoadStagedAutoReset(newlyStagedItemId, setNewlyStagedItemId);

  // Use ref for stagedLineItems to avoid recreating callbacks when items change
  const stagedLineItemsRef = React.useRef(stagedLineItems);
  React.useEffect(() => {
    stagedLineItemsRef.current = stagedLineItems;
  }, [stagedLineItems]);

  // Handle pricing unit updates based on toggle state and requested items
  usePricingUnitSync({
    stagedLineItems,
    requestedLineItems,
    weightDisplayUnitPreference,
    inventoryItemsById,
    onUpdateItem: updateItem,
  });

  const handleSelectMaterial = React.useCallback(
    (itemId: string) => {
      const lineItem = requestedLineItems.find((item) => item.id === itemId);

      if (!lineItem) return;

      // Use ref to get current staged items without causing callback recreation
      const currentStagedItems = stagedLineItemsRef.current;
      const stagedTagIds = new Set(
        currentStagedItems
          .filter((staged) => staged.itemId === lineItem.itemId && staged.tagId)
          .map((staged) => staged.tagId),
      );

      const nextAvailableTag = lineItem.aggregatedTags?.find(
        (tag) => !stagedTagIds.has(tag.id),
      );

      const tagIdToUse = nextAvailableTag?.id ?? lineItem.tagId;

      const stagedTagWeight =
        nextAvailableTag?.netQuantity ??
        (lineItem.itemId && tagIdToUse
          ? (inventoryTagOptionsByItemId
              ?.get(lineItem.itemId)
              ?.find((tag) => tag.id === tagIdToUse)?.tagData?.netQuantity ?? 0)
          : 0);

      const newItemId = `temp-${uuidv4()}-${lineItem.id}`;

      addItem?.(lineItem.type ?? 'Raw', {
        ...lineItem,
        id: newItemId,
        tagId: tagIdToUse,
        phase: stagedPhase,
        aggregatedTags: undefined,
        weights: createDefaultWeights(enumUnit.Pounds, {
          gross: stagedTagWeight,
          tare: 0,
          net: stagedTagWeight,
        }),
      });

      setNewlyStagedItemId(newItemId);
    },
    [requestedLineItems, inventoryTagOptionsByItemId, addItem, stagedPhase],
  );

  const handleCapturePhoto = React.useCallback(
    (lineItemId: string, previewUrl: string) => {
      const existingItem = stagedLineItemsRef.current.find(
        (item) => item.id === lineItemId,
      );
      if (existingItem?.photo?.previewUrl) {
        URL.revokeObjectURL(existingItem.photo.previewUrl);
      }

      updateItem(lineItemId, {
        photo: {
          previewUrl,
          status: 'captured',
          capturedAt: new Date().toISOString(),
        },
      });
    },
    [updateItem],
  );

  return (
    <div className={cn('@LoadStaged', 'min-h-0 flex-1 overflow-hidden')}>
      <div className="flex h-full w-full gap-2">
        <div className={'flex min-h-0 flex-1 flex-col gap-2'}>
          <Widget
            className={cn(
              '@StageMaterialsWidget',
              'flex min-h-0 w-full min-w-[765px] flex-1 flex-col overflow-hidden',
            )}
          >
            <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto rounded-lg px-3 py-2 outline-2 outline-white">
              <StagedItemsList
                stagedLineItems={stagedLineItems}
                disabled={disabled}
                facilityDefaultUnit={facilityDefaultUnit}
                newlyStagedItemId={newlyStagedItemId}
                getLineItemWeightUnit={getLineItemWeightUnit}
                onUpdateItem={updateItem}
                onDeleteItem={deleteItem}
              />
            </div>
          </Widget>

          <Widget
            className={
              'relative flex flex-shrink-0 items-center gap-1.5 p-1.5 text-center'
            }
          >
            <MaterialsWidget
              onAddItem={addItem}
              onUpdateItem={updateItem}
              highlightedMaterialIds={highlightedMaterialIds}
              highlightedSectionTitle={highlightedSectionTitle}
            />
          </Widget>

          <LoadStagedFooter
            onSaveUpdates={onSaveUpdates}
            onDiscard={onDiscard}
            isSaving={isSaving}
            isDisabled={disabled}
          />
        </div>

        <div className={'flex flex-shrink-0 flex-col gap-2 2xl:w-[250px]'}>
          <ScaleAndCameraWidget onCapturePhoto={handleCapturePhoto} />
          <RequestedMaterialsList
            getLineItemWeightUnit={getLineItemWeightUnit}
            facilityDefaultUnit={facilityDefaultUnit}
            onSelectMaterial={handleSelectMaterial}
          />
        </div>
      </div>

      <LineItemPhotoModal stagedLineItems={stagedLineItems} />
    </div>
  );
};
